import React, { useState, useRef } from 'react';
import Popup from './Popup';

const NODES = [
  {
    id: 'discovery', icon: '🔍', label: 'Discover\nApps',
    description: 'Scans all legacy applications in the portfolio. Identifies programming languages, frameworks, file counts, and flags known security and quality issues.',
    what: 'Automated portfolio analysis — no AI needed',
    subStageKey: 'discover',
  },
  {
    id: 'rationalization', icon: '🧠', label: 'Analyze\n& Score',
    description: 'AI analyses each application in parallel and recommends the best modernization path — Retire, Refactor, or Rebuild — with a risk score and business justification.',
    what: 'Parallel AI analysis per application',
    subStageKey: 'rationalize',
  },
  {
    id: 'supervisor', icon: '🤖', label: 'Plan\nthe Work',
    description: 'Reviews AI recommendations and automatically routes clear-cut decisions. Only escalates genuinely uncertain cases to you for review — saving your time.',
    what: 'Autonomous decision routing',
  },
  {
    id: 'hitl_decision', icon: '⏸', label: 'Review\n& Decide', hitl: true,
    description: 'Pipeline pauses only when human judgement is needed. Review AI recommendations and confirm or override the modernization path for each application.',
    what: 'Human review checkpoint (only when needed)',
  },
  {
    id: 'requirements', icon: '📝', label: 'Define\nRequirements', hitl: true,
    description: 'AI drafts a full requirements document for each application — goals, technical specs, API design, and acceptance criteria. You review and edit before work begins.',
    what: 'AI drafting + human approval checkpoint',
    subStageKey: 'requirements',
  },
  {
    id: 'coding', icon: '💻', label: 'Build\nNew Code',
    description: 'AI generates the complete modernized application code per application. Focuses purely on production code — clean, testable, and well-structured.',
    what: 'AI code generation — writes output to disk',
    subStageKey: 'coding',
  },
  {
    id: 'test_writing', icon: '✍️', label: 'Write\nTests',
    description: 'Dedicated test agents write comprehensive unit tests for each app using the native test framework — pytest, JUnit, xUnit, or Jest based on the modernized stack.',
    what: 'Stack-specific test generation (Python/Java/C#/Node)',
  },
  {
    id: 'testing', icon: '🧪', label: 'Run\nTests',
    description: 'Runs automated tests on each generated application. Reports how many tests passed, failed, and what percentage of the code is covered.',
    what: 'Automated test execution per application',
    subStageKey: 'testing',
  },
  {
    id: 'security', icon: '🔒', label: 'Scan\nfor Risks',
    description: 'Scans each generated application for security vulnerabilities — injection risks, weak cryptography, hardcoded secrets, and other common issues.',
    what: 'Automated security scanning per application',
    subStageKey: 'security',
  },
  {
    id: 'quality_gate', icon: '🔬', label: 'Approve\nor Retry',
    description: 'Evaluates test and security results. If coverage is too low or security issues are found, automatically sends the code back for improvement — up to 3 times.',
    what: 'Automated quality gate with retry loop',
  },
  {
    id: 'iac', icon: '🏗️', label: 'Generate\nInfrastructure',
    description: 'Generates cloud infrastructure scripts to deploy all modernized applications — covering compute, database, load balancing, networking, and access controls.',
    what: 'Cloud infrastructure generation (Terraform + CDK)',
  },
  {
    id: 'audit', icon: '📋', label: 'Audit\n& Complete',
    description: 'Produces a complete modernization report — decisions made, quality results, security findings, retry history, and deployment status for every application.',
    what: 'Full audit trail and summary report',
  },
];

const IAC_NODES = [
  { id: 'iac_terraform', icon: '🟧', label: 'Terraform', description: 'Generates AWS infrastructure scripts for compute, database, and networking.', what: 'Terraform HCL' },
  { id: 'iac_cdk',       icon: '📦', label: 'CDK',       description: 'Generates AWS CDK TypeScript for the same infrastructure resources.', what: 'AWS CDK TypeScript' },
];

const STATUS_COLOR = {
  active: '#0969DA', done: '#1A7F37', error: '#CF222E',
  warn: '#9A6700', skip: '#D0D7DE', idle: '#D0D7DE',
};

// ── Node popup content ────────────────────────────────────────────────────────
function NodePopupContent({ node, status, onClose }) {
  const color = STATUS_COLOR[status === 'active' && node.hitl ? 'warn' : status] || '#57606A';
  const label = { active: '⟳ Running now…', done: '✅ Complete', error: '❌ Error', idle: '· Waiting' }[status] || '· Waiting';

  return (
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{node.icon} {node.label.replace('\n', ' ')}</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#57606A' }}>×</button>
      </div>
      <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: color + '18', color, marginBottom: 10 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#57606A', lineHeight: 1.6, marginBottom: 10 }}>{node.description}</div>
      <div style={{ fontSize: 11, fontFamily: 'monospace', background: '#F6F8FA', border: '1px solid #D0D7DE', borderRadius: 6, padding: '6px 10px', color: '#0969DA' }}>{node.what}</div>
    </div>
  );
}

// ── Sub-stage mini pill (shown below node) ────────────────────────────────────
function SubStagePill({ appName, status }) {
  const s = status || 'idle';
  const color = STATUS_COLOR[s] || '#D0D7DE';
  const bg    = s === 'active' ? '#DDF4FF' : s === 'done' ? '#DAFBE1' : s === 'error' ? '#FFEBE9' : '#F6F8FA';
  const icon  = s === 'active' ? <span className="spin" style={{ display: 'inline-block', fontSize: 9 }}>⟳</span>
              : s === 'done'   ? '✓'
              : s === 'error'  ? '✗'
              : s === 'skip'   ? '—'
              : '·';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
      border: `1px solid ${color}`, background: bg, color,
      whiteSpace: 'nowrap',
      animation: s === 'active' ? 'lane-stage-pulse 1.4s ease infinite' : 'none',
    }}>
      <span>{icon}</span>
      <span style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {appName.replace('legacy-', '')}
      </span>
    </div>
  );
}

// ── Node column (circle + sub-stages below) ───────────────────────────────────
function NodeCircle({ node, status, nodeTimes, lanes, onStageClick, selectedStage }) {
  const [anchorRect, setAnchorRect] = useState(null);
  const s          = status || 'idle';
  const ringClass  = node.hitl && s === 'active' ? 'hitl' : s;
  const isActive   = s === 'active';
  const isDone     = s === 'done';
  const isSelected = selectedStage === node.id;
  const elapsed    = nodeTimes?.[node.id];

  const icon = isActive ? <span className="spin">⟳</span>
             : isDone   ? '✅'
             : s === 'error' ? '❌'
             : node.icon;

  const handleCircleClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setAnchorRect(prev => prev ? null : rect);
    onStageClick?.(node.id);
  };

  const subStages = node.subStageKey && lanes?.length > 0
    ? lanes.map(lane => ({ name: lane.name, status: lane.stages?.[node.subStageKey] || 'idle' }))
    : [];
  const hasSubActivity = subStages.some(s => s.status !== 'idle');

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Main circle */}
      <div className="node-circle" onClick={handleCircleClick} style={{ cursor: 'pointer' }}>
        <div className={`node-ring ${ringClass}`}
          style={isSelected ? { outline: '3px solid #0969DA', outlineOffset: 3 } : {}}>
          {icon}
        </div>
        <div className="node-label">
          {node.label.split('\n').map((l, i) => <div key={i}>{l}</div>)}
        </div>
        {isDone && elapsed && (
          <div style={{ fontSize: 9, color: 'var(--green)', fontWeight: 600, marginTop: 2 }}>⏱ {elapsed}</div>
        )}
      </div>

      {/* Sub-stage branch */}
      {hasSubActivity && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 6 }}>
          <div style={{ width: 2, height: 12, background: '#D0D7DE' }} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 2, height: 6, background: '#D0D7DE' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
              {subStages.map(sub => (
                <div key={sub.name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: 10, height: 2, background: '#D0D7DE' }} />
                    <div style={{ width: 0, height: 0, borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft: '5px solid #D0D7DE' }} />
                  </div>
                  <SubStagePill appName={sub.name} status={sub.status} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Portal popup — always on top, never clipped */}
      {anchorRect && (
        <Popup anchorRect={anchorRect} onClose={() => setAnchorRect(null)} width={270}>
          <NodePopupContent node={node} status={s} onClose={() => setAnchorRect(null)} />
        </Popup>
      )}
    </div>
  );
}

function Connector({ fromStatus }) {
  const cls = fromStatus === 'done' ? 'done' : fromStatus === 'active' ? 'active' : '';
  return (
    <div className={`node-connector ${cls}`} style={{ alignSelf: 'flex-start', marginTop: 30 }}>
      <span className="node-connector-arrow">›</span>
    </div>
  );
}

export default function PipelineGraph({ nodeStatuses, lanes, nodeTimes, onStageClick, selectedStage }) {
  const iacStarted = Object.keys(nodeStatuses).some(k => k.startsWith('iac_'));

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title-accent">🔀</span> Modernization Workflow
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--txt2)', fontWeight: 400 }}>
          Click any step for details · arrows show per-app progress
        </span>
      </div>

      <style>{`
        @keyframes lane-stage-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(9,105,218,0.35); }
          50%       { box-shadow: 0 0 0 5px rgba(9,105,218,0); }
        }
      `}</style>

      <div className="pipeline-wrapper">
        {/* Main pipeline */}
        <div style={{ marginBottom: 8 }}>
          <div className="pipeline-label">Modernization Workflow</div>
          <div className="pipeline-row">
            {NODES.map((node, i) => (
              <React.Fragment key={node.id}>
                <NodeCircle
                  node={node}
                  status={nodeStatuses[node.id]}
                  nodeTimes={nodeTimes}
                  lanes={lanes}
                  onStageClick={onStageClick}
                  selectedStage={selectedStage}
                />
                {i < NODES.length - 1 && <Connector fromStatus={nodeStatuses[node.id]} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* IaC sub-agent */}
        {iacStarted && (
          <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 14, marginTop: 4 }}>
            <div className="pipeline-label" style={{ color: 'var(--teal)' }}>Cloud Infrastructure (parallel)</div>
            <div className="pipeline-row">
              {IAC_NODES.map((node, i) => (
                <React.Fragment key={node.id}>
                  <NodeCircle node={node} status={nodeStatuses[node.id]} />
                  {i < IAC_NODES.length - 1 && <Connector fromStatus={nodeStatuses[node.id]} />}
                </React.Fragment>
              ))}
              <Connector fromStatus={nodeStatuses['iac_cdk']} />
              <div className="node-circle">
                <div className={`node-ring ${nodeStatuses['iac_cdk'] === 'done' ? 'done' : 'idle'}`}>🏁</div>
                <div className="node-label">Done</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
